import {
    ActivitySeries,
    HistoryCountsBreakdown,
} from "@common/schemas/domains/activity/activity-timeline.schema";
import type { UserSchema } from "@common/schemas/user.schema";
import type { HistoryFilter } from "@common/types/auditLog";
import type { TimelineSeriesKey } from "./activityTimelineSeries.lib";

/** Updated by with more people than this keeps the single People series. */
export const MAX_TIMELINE_PEOPLE = 6;

/** A member the timeline can chart on their own. */
export type TimelinePerson = Pick<
    UserSchema,
    "id" | "name" | "email" | "iconColor" | "avatar"
>;

/** The people picked one by one in Updated by; none with All people picked, since People then counts everyone. */
export function toPickedPersonIds({
    actorIds,
    series,
}: {
    actorIds?: ReadonlyArray<number | null>;
    series?: readonly ActivitySeries[];
}): number[] {
    if (series?.includes(ActivitySeries.PEOPLE)) {
        return [];
    }
    return (actorIds ?? []).flatMap((id) => (id === null ? [] : [id]));
}

/** The Updated by picks a slice click sets. */
export interface SliceActorPicks {
    actorIds: number[] | undefined;
    series: ActivitySeries[] | undefined;
}

/**
 * Updated by for a clicked slice: exactly its series, so the list holds what
 * the slice counts. A person's segment narrows to that person, and People
 * keeps the explicitly picked people, else it means All people.
 */
export function toSliceActorPicks({
    key: { series, personId },
    filter,
}: {
    key: TimelineSeriesKey;
    filter: Pick<HistoryFilter, "actorIds" | "series">;
}): SliceActorPicks {
    if (personId !== undefined) {
        return { actorIds: [personId], series: undefined };
    }
    const pickedPersonIds = toPickedPersonIds(filter);
    if (series === ActivitySeries.PEOPLE && pickedPersonIds.length > 0) {
        return { actorIds: pickedPersonIds, series: undefined };
    }
    return { actorIds: undefined, series: [series] };
}

/**
 * The people to chart one by one: the members picked in Updated by, in pick
 * order, when that's 1 to 6 of them. Empty keeps the single People series,
 * also when a picked person isn't in `members` (e.g. no longer a member),
 * since People counts them but no segment would.
 */
export function toTimelinePeople({
    actorIds,
    series,
    members,
}: {
    actorIds: ReadonlyArray<number | null> | undefined;
    series?: readonly ActivitySeries[];
    members: readonly TimelinePerson[];
}): TimelinePerson[] {
    const membersById = new Map(members.map((member) => [member.id, member]));
    const pickedIds = [...new Set(toPickedPersonIds({ actorIds, series }))];
    const pickedPeople = pickedIds.flatMap((id) => {
        const member = membersById.get(id);
        return member ? [member] : [];
    });
    const isEveryPickResolved = pickedPeople.length === pickedIds.length;
    return isEveryPickResolved && pickedPeople.length <= MAX_TIMELINE_PEOPLE
        ? pickedPeople
        : [];
}

/** Per-person counts only when there are people to chart. */
export const toTimelineBreakdown = (people: readonly TimelinePerson[]) =>
    people.length > 0
        ? HistoryCountsBreakdown.SERIES_AND_PEOPLE
        : HistoryCountsBreakdown.SERIES;
